import React, { useState, useRef, useMemo, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { companiesData, projectsData } from '../data/companiesData';
import ProjectCard from '../components/projectCard/ProjectCard';
import CompanyCard from '../components/companyCard/CompanyCard';
import { Helmet } from 'react-helmet-async';
import { FaChevronLeft, FaChevronRight, FaMapMarkerAlt } from 'react-icons/fa';
import { cities } from '../data/citiesData';

/* =========================================================
   🔍 SEO CONSTANTS
   ========================================================= */
const SITE_URL = 'https://www.estateghar.com'; // ⚠️ change to your real domain
const SITE_NAME = 'EstateGhar';
const CANONICAL_BASE = `${SITE_URL}/projects`;

/* =========================================================
   City priority order — Karachi first, then alphabetical
   ========================================================= */
const PRIORITY_CITY = 'karachi';

/* =========================================================
   Utility: slugify (hoisted)
   ========================================================= */
function slugify(text) {
  return String(text)
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

const ProjectsPage = () => {
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const navigate = useNavigate();
  const carouselRef = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();

  /* ---------------- 🔍 SEO: hydrate filters from URL on mount ---------------- */
  useEffect(() => {
    const urlCity = searchParams.get('city');
    const urlSearch = searchParams.get('q');
    if (urlCity) setSelectedCity(urlCity.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()));
    if (urlSearch) setSearchQuery(urlSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ---------------- 🔍 SEO: keep URL in sync with filters ---------------- */
  useEffect(() => {
    const next = new URLSearchParams();
    if (selectedCity) next.set('city', slugify(selectedCity));
    if (searchQuery) next.set('q', searchQuery);
    const nextStr = next.toString();
    const currentStr = searchParams.toString();
    if (nextStr !== currentStr) {
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCity, searchQuery]);

  // Normalize the imported cities into a simple array of strings
  const cityList = useMemo(() => {
    if (!Array.isArray(cities)) return [];
    return cities
      .map((c) => {
        if (typeof c === 'string') return c;
        return c?.name || c?.city || c?.title || '';
      })
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b));
  }, []);

  const filteredProjects = useMemo(() => {
    return projectsData
      .filter((project) => !selectedCompany || project.companyId === selectedCompany.id)
      .filter(
        (project) =>
          !searchQuery ||
          project.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          project.title?.toLowerCase().includes(searchQuery.toLowerCase())
      )
      .filter(
        (project) =>
          !selectedCity || project.city?.toLowerCase() === selectedCity.toLowerCase()
      );
  }, [selectedCompany, searchQuery, selectedCity]);

  /* ---------------- GROUP BY CITY (Karachi first) ---------------- */
  const projectsByCity = useMemo(() => {
    const grouped = {};

    filteredProjects.forEach((project) => {
      const rawCity = (project.city || project.location || '').trim();
      const cityName = rawCity
        ? rawCity
            .split(/\s+/)
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ')
        : 'Other Locations';

      if (!grouped[cityName]) {
        grouped[cityName] = [];
      }
      grouped[cityName].push(project);
    });

    /* =========================================================
       🔍 SEO + UX: Sort cities with priority
       1. Karachi always first (when present)
       2. "Other Locations" always last
       3. Everything else alphabetically
       ========================================================= */
    const sortedCities = Object.keys(grouped).sort((a, b) => {
      const aIsKarachi = a.toLowerCase() === PRIORITY_CITY;
      const bIsKarachi = b.toLowerCase() === PRIORITY_CITY;
      if (aIsKarachi && !bIsKarachi) return -1;
      if (!aIsKarachi && bIsKarachi) return 1;

      const aIsOther = a === 'Other Locations';
      const bIsOther = b === 'Other Locations';
      if (aIsOther && !bIsOther) return 1;
      if (!aIsOther && bIsOther) return -1;

      return a.localeCompare(b);
    });

    return sortedCities.map((cityName) => ({
      cityName,
      citySlug: slugify(cityName),
      projects: grouped[cityName],
      count: grouped[cityName].length,
    }));
  }, [filteredProjects]);

  const handleCompanySelect = (company) => {
    setSelectedCompany(company);
    setCurrentIndex(0);
    setSearchQuery('');
    setSelectedCity('');
  };

  const handleProjectSelect = (project) => {
    navigate(`/projects/${project.route}`);
  };

  const handleBackToCompanies = () => {
    setSelectedCompany(null);
    setCurrentIndex(0);
    setSearchQuery('');
    setSelectedCity('');
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setSelectedCity('');
  };

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev === companiesData.length - 1 ? 0 : prev + 1));
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev === 0 ? companiesData.length - 1 : prev - 1));
  };

  const hasActiveFilters = Boolean(searchQuery || selectedCity);

  /* =========================================================
     🔍 SEO: Titles, description, canonical, JSON-LD
     ========================================================= */
  const citiesList = projectsByCity
    .map((g) => g.cityName)
    .filter((c) => c !== 'Other Locations');
  const totalProjects = filteredProjects.length;

  const getPageTitle = () => {
    // Filters and dynamic views get "noindex" titles (see robots below)
    if (selectedCompany && selectedCity) {
      return `${selectedCompany.name} Projects in ${selectedCity} | ${SITE_NAME}`;
    }
    if (selectedCompany) {
      return `${selectedCompany.name} Projects in Pakistan | ${SITE_NAME}`;
    }
    if (selectedCity) {
      return `Real Estate Projects in ${selectedCity} | ${SITE_NAME}`;
    }
    if (searchQuery) {
      return `Search: "${searchQuery}" | Projects | ${SITE_NAME}`;
    }
    // Base page — target the head keyword
    return `Real Estate Projects in Pakistan — Karachi, Lahore, Islamabad | ${SITE_NAME}`;
  };

  const getMetaDescription = () => {
    const base = `Explore ${totalProjects} real estate and construction projects across Pakistan`;
    if (selectedCity) {
      return `${base} in ${selectedCity}. Find residential communities, commercial developments, and investment opportunities with ${SITE_NAME}.`;
    }
    if (citiesList.length > 0) {
      return `${base}. Browse projects in ${citiesList.slice(0, 6).join(', ')}. Discover the future of property investment with ${SITE_NAME}.`;
    }
    return `${base}. Find residential communities, commercial developments, and investment opportunities with ${SITE_NAME}.`;
  };

  const getKeywords = () => {
    const base = [
      'real estate projects Pakistan',
      'property development Pakistan',
      'investment opportunities Pakistan',
      'new housing societies Karachi',
      'new housing societies Lahore',
      'new housing societies Islamabad',
      'construction projects Pakistan',
      SITE_NAME,
    ];
    if (selectedCompany) base.push(selectedCompany.name, `${selectedCompany.name} projects`);
    if (selectedCity) base.push(`projects in ${selectedCity}`, `${selectedCity} real estate`);
    citiesList.forEach((c) =>
      base.push(`projects in ${c}`, `property in ${c}`)
    );
    return base.join(', ');
  };

  // Canonical only reflects clean base page — filtered views are noindex
  const canonicalUrl = CANONICAL_BASE;

  // Filtered views get noindex to prevent duplicate content
  const robotsMeta = hasActiveFilters
    ? 'noindex, follow, max-image-preview:large'
    : 'index, follow, max-image-preview:large, max-snippet:-1';

  /* ---------------- JSON-LD STRUCTURED DATA ---------------- */
  const structuredData = useMemo(() => {
    const itemListElements = filteredProjects.slice(0, 20).map((project, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item: {
        '@type': 'RealEstateProject',
        name: project.name || project.title,
        url: `${SITE_URL}/projects/${project.route}`,
        image: project.image || project.thumbnail,
        address: {
          '@type': 'PostalAddress',
          addressLocality: project.city,
          addressCountry: 'PK',
        },
      },
    }));

    const itemList = {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: getPageTitle(),
      description: getMetaDescription(),
      numberOfItems: totalProjects,
      itemListElement: itemListElements,
    };

    const breadcrumb = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
        { '@type': 'ListItem', position: 2, name: 'Projects', item: CANONICAL_BASE },
        ...(selectedCity
          ? [
              {
                '@type': 'ListItem',
                position: 3,
                name: selectedCity,
                item: `${CANONICAL_BASE}?city=${slugify(selectedCity)}`,
              },
            ]
          : []),
      ],
    };

    const org = {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: SITE_NAME,
      url: SITE_URL,
      logo: `${SITE_URL}/logo.png`,
    };

    const faq = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'How many real estate projects are listed on EstateGhar?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: `EstateGhar currently showcases ${projectsData.length}+ real estate and construction projects across Pakistan, including Karachi, Lahore, Islamabad, and other major cities.`,
          },
        },
        {
          '@type': 'Question',
          name: 'Which cities in Pakistan have the most real estate projects?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Karachi, Lahore, and Islamabad are the leading cities for new real estate and construction projects in Pakistan, followed by Rawalpindi, Faisalabad, and Multan.',
          },
        },
        {
          '@type': 'Question',
          name: 'Are these real estate projects approved and verified?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes, we only list projects from reputed developers. Always verify approvals from relevant authorities such as DHA, CDA, LDA, SBCA, or KDA before investing.',
          },
        },
      ],
    };

    return [itemList, breadcrumb, org, faq];
  }, [filteredProjects, totalProjects, selectedCity]);

  // Shared glass surface classes
  const glassSurface =
    'bg-white/60 backdrop-blur-xl border border-white/60 shadow-[0_8px_32px_0_rgba(31,38,135,0.10)] rounded-2xl';
  const glassInput =
    'w-full pl-10 pr-10 py-3 bg-white/50 backdrop-blur-md border border-white/60 rounded-xl text-gray-800 placeholder-gray-500 shadow-sm focus:outline-none focus:ring-2 focus:ring-amber-400/60 focus:border-amber-300/60 focus:bg-white/70 transition-all';

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-amber-50 via-rose-50 to-sky-100 py-8">
      {/* Decorative blurred orbs */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-32 -left-24 h-96 w-96 rounded-full bg-amber-300/40 blur-3xl" />
        <div className="absolute top-1/3 -right-24 h-96 w-96 rounded-full bg-rose-300/40 blur-3xl" />
        <div className="absolute bottom-0 left-1/4 h-96 w-96 rounded-full bg-sky-300/40 blur-3xl" />
      </div>

      {/* =========================================================
          🔍 SEO: Helmet head
          ========================================================= */}
      <Helmet>
        <html lang="en-PK" />
        <title>{getPageTitle()}</title>
        <meta name="description" content={getMetaDescription()} />
        <meta name="keywords" content={getKeywords()} />
        <meta name="robots" content={robotsMeta} />
        <meta name="author" content={SITE_NAME} />
        <meta name="theme-color" content="#f59e0b" />
        <link rel="canonical" href={canonicalUrl} />

        {/* Geo signals — Pakistan */}
        <meta name="geo.region" content="PK" />
        <meta name="geo.placename" content="Pakistan" />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content={SITE_NAME} />
        <meta property="og:title" content={getPageTitle()} />
        <meta property="og:description" content={getMetaDescription()} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:locale" content="en_PK" />
        <meta property="og:image" content={`${SITE_URL}/og-projects.jpg`} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta
          property="og:image:alt"
          content="Real estate projects in Pakistan by EstateGhar"
        />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={getPageTitle()} />
        <meta name="twitter:description" content={getMetaDescription()} />
        <meta name="twitter:image" content={`${SITE_URL}/twitter-projects.jpg`} />

        {/* hreflang */}
        <link rel="alternate" hrefLang="en-PK" href={canonicalUrl} />
        <link rel="alternate" hrefLang="x-default" href={canonicalUrl} />

        {/* JSON-LD */}
        {structuredData.map((schema, index) => (
          <script key={index} type="application/ld+json">
            {JSON.stringify(schema)}
          </script>
        ))}
      </Helmet>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <header className={`text-center mb-10 p-8 md:p-10 ${glassSurface}`}>
          <div className="inline-block bg-amber-400/30 backdrop-blur-md border border-amber-300/50 text-amber-800 px-4 py-1 rounded-full text-sm font-semibold mb-3 shadow-sm">
            INVEST WITH US
          </div>
          {/* 🔍 SEO: single H1 */}
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4 drop-shadow-sm">
            {selectedCity
              ? `Projects in ${selectedCity}`
              : 'Real Estate Projects in Pakistan'}
          </h1>
          <div className="w-20 h-1 bg-gradient-to-r from-amber-400 to-rose-400 mx-auto mb-4 rounded-full"></div>
          <p className="text-lg text-gray-700 max-w-3xl mx-auto">
            {selectedCity
              ? `Explore ${totalProjects} real estate ${
                  totalProjects === 1 ? 'project' : 'projects'
                } in ${selectedCity}. Discover the future of property investment with ${SITE_NAME}.`
              : 'Explore real estate and construction projects across Pakistan — from Karachi and Lahore to Islamabad and beyond. From residential communities to commercial developments, discover the future of property investment with EstateGhar.'}
          </p>

          {/* 🔍 SEO: internal links — city quick links (Karachi first) */}
          {!selectedCompany && citiesList.length > 1 && (
            <nav
              aria-label="Browse projects by city"
              className="flex flex-wrap justify-center gap-2 mt-6"
            >
              <span className="text-sm text-gray-600 self-center mr-2">
                Browse by city:
              </span>
              {projectsByCity.slice(0, 8).map((group) => (
                <button
                  key={group.citySlug}
                  onClick={() => {
                    setSelectedCity(group.cityName);
                    document
                      .getElementById('projects-grid')
                      ?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="inline-flex items-center gap-1 px-3 py-1 bg-white/60 backdrop-blur-md border border-white/60 text-amber-800 text-sm rounded-full hover:bg-amber-400/40 hover:border-amber-300/60 transition-all shadow-sm"
                  aria-label={`View projects in ${group.cityName}`}
                >
                  <FaMapMarkerAlt className="w-3 h-3" aria-hidden="true" />
                  {group.cityName} ({group.count})
                </button>
              ))}
            </nav>
          )}
        </header>

        {/* Breadcrumb */}
        {selectedCompany && (
          <div className={`flex items-center space-x-2 mb-6 px-4 py-3 ${glassSurface}`}>
            <button
              onClick={handleBackToCompanies}
              className="text-amber-700 hover:text-amber-900 font-medium flex items-center gap-1 transition-colors"
            >
              ← Back to Companies
            </button>
            <span className="text-gray-400">|</span>
            <span className="text-gray-800 font-semibold">{selectedCompany.name}</span>
          </div>
        )}

        {/* Companies Carousel — Desktop */}
        {!selectedCompany && (
          <section
            className="hidden md:block mb-16"
            aria-labelledby="partner-companies-desktop"
          >
            <div className={`flex items-center justify-between mb-6 px-6 py-4 ${glassSurface}`}>
              <h2
                id="partner-companies-desktop"
                className="text-2xl font-bold text-gray-900"
              >
                Our Partner Companies
              </h2>
              <div className="flex gap-2">
                <button
                  onClick={prevSlide}
                  className="p-2 rounded-full bg-white/60 backdrop-blur-md border border-white/60 text-gray-700 hover:bg-amber-400/70 hover:text-white hover:border-amber-300/60 shadow-sm transition-all duration-300"
                  aria-label="Previous companies"
                >
                  <FaChevronLeft className="w-4 h-4" aria-hidden="true" />
                </button>
                <button
                  onClick={nextSlide}
                  className="p-2 rounded-full bg-white/60 backdrop-blur-md border border-white/60 text-gray-700 hover:bg-amber-400/70 hover:text-white hover:border-amber-300/60 shadow-sm transition-all duration-300"
                  aria-label="Next companies"
                >
                  <FaChevronRight className="w-4 h-4" aria-hidden="true" />
                </button>
              </div>
            </div>
            <div className="relative overflow-hidden py-2">
              <div
                className="flex transition-transform duration-500 ease-in-out"
                style={{ transform: `translateX(-${currentIndex * 25}%)` }}
              >
                {companiesData.map((company) => (
                  <div key={company.id} className="w-1/4 flex-shrink-0 px-3">
                    <CompanyCard company={company} onSelect={handleCompanySelect} />
                  </div>
                ))}
              </div>
            </div>
            <div className="flex justify-center mt-6 gap-2">
              {companiesData.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentIndex(idx)}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    idx === currentIndex
                      ? 'bg-amber-500 w-6 shadow-md'
                      : 'bg-white/70 w-2 hover:bg-white'
                  }`}
                  aria-label={`Go to slide ${idx + 1}`}
                />
              ))}
            </div>
          </section>
        )}

        {/* Companies Grid — Mobile */}
        {!selectedCompany && (
          <section className="md:hidden mb-12" aria-labelledby="partner-companies-mobile">
            <h2
              id="partner-companies-mobile"
              className={`text-xl font-bold text-gray-900 mb-4 px-6 py-4 ${glassSurface}`}
            >
              Our Partner Companies
            </h2>
            <div className="grid grid-cols-1 gap-4">
              {companiesData.map((company) => (
                <CompanyCard
                  key={company.id}
                  company={company}
                  onSelect={handleCompanySelect}
                />
              ))}
            </div>
          </section>
        )}

        {/* Projects Section */}
        <section id="projects-grid" className="scroll-mt-6">
          {selectedCompany && (
            <div className={`flex items-center justify-between mb-6 px-6 py-4 ${glassSurface}`}>
              <h2 className="text-2xl font-bold text-gray-900">
                Projects by {selectedCompany.name}
              </h2>
            </div>
          )}

          {/* Filters Bar */}
          <div className={`${glassSurface} p-4 md:p-5 mb-8`}>
            <div className="flex flex-col md:flex-row gap-4">
              {/* Search */}
              <div className="flex-1 relative">
                <label htmlFor="project-search" className="sr-only">
                  Search by project name
                </label>
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg
                    className="h-5 w-5 text-gray-500"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <input
                  id="project-search"
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by project name..."
                  className={glassInput}
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700"
                    aria-label="Clear search"
                  >
                    <svg
                      className="h-5 w-5"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                )}
              </div>

              {/* City Filter */}
              <div className="flex-1 relative">
                <label htmlFor="city-filter" className="sr-only">
                  Filter by city
                </label>
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg
                    className="h-5 w-5 text-gray-500"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <select
                  id="city-filter"
                  value={selectedCity}
                  onChange={(e) => setSelectedCity(e.target.value)}
                  className={`${glassInput} appearance-none cursor-pointer`}
                >
                  <option value="">All Cities</option>
                  {cityList.map((city) => (
                    <option key={city} value={city}>
                      {city}
                    </option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                  <svg
                    className="h-5 w-5 text-gray-500"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
              </div>

              {/* Clear */}
              {hasActiveFilters && (
                <button
                  onClick={handleClearFilters}
                  className="flex items-center justify-center gap-2 px-5 py-3 text-gray-700 hover:text-amber-800 font-medium rounded-xl bg-white/50 backdrop-blur-md border border-white/60 hover:border-amber-300/70 hover:bg-white/70 shadow-sm transition-all whitespace-nowrap"
                >
                  <svg
                    className="h-4 w-4"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Clear
                </button>
              )}
            </div>

            {/* Active Filters Summary */}
            {hasActiveFilters && (
              <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t border-white/60">
                <span className="text-sm text-gray-600 font-medium">Active filters:</span>
                {searchQuery && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-amber-400/30 backdrop-blur-md border border-amber-300/50 text-amber-900 text-sm rounded-full shadow-sm">
                    Name: "{searchQuery}"
                    <button
                      onClick={() => setSearchQuery('')}
                      className="hover:text-amber-950"
                      aria-label="Remove name filter"
                    >
                      <svg
                        className="h-3.5 w-3.5"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        aria-hidden="true"
                      >
                        <path
                          fillRule="evenodd"
                          d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </button>
                  </span>
                )}
                {selectedCity && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-rose-400/30 backdrop-blur-md border border-rose-300/50 text-rose-900 text-sm rounded-full shadow-sm">
                    City: {selectedCity}
                    <button
                      onClick={() => setSelectedCity('')}
                      className="hover:text-rose-950"
                      aria-label="Remove city filter"
                    >
                      <svg
                        className="h-3.5 w-3.5"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        aria-hidden="true"
                      >
                        <path
                          fillRule="evenodd"
                          d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </button>
                  </span>
                )}
                <span className="text-sm text-gray-600 ml-auto">
                  {filteredProjects.length}{' '}
                  {filteredProjects.length === 1 ? 'project' : 'projects'} found
                </span>
              </div>
            )}
          </div>

          {/* Results count */}
          {filteredProjects.length > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-amber-400/30 backdrop-blur-md border border-amber-300/50 text-amber-900 text-sm font-semibold rounded-full shadow-sm">
                {filteredProjects.length}{' '}
                {filteredProjects.length === 1 ? 'Project' : 'Projects'} Found
              </span>
              <span className="inline-flex items-center gap-1 text-sm text-gray-600">
                <FaMapMarkerAlt className="w-3.5 h-3.5" aria-hidden="true" />
                {projectsByCity.length}{' '}
                {projectsByCity.length === 1 ? 'City' : 'Cities'}
              </span>
            </div>
          )}

          {/* ====== PROJECTS GROUPED BY CITY (Karachi first) ====== */}
          {filteredProjects.length > 0 ? (
            <div>
              {projectsByCity.map((group, groupIndex) => (
                <section
                  key={group.citySlug}
                  id={`city-${group.citySlug}`}
                  className="scroll-mt-6 mb-12"
                  aria-labelledby={`city-heading-${group.citySlug}`}
                >
                  <div className="flex flex-wrap items-center justify-between gap-3 mb-5 pb-3 border-b-2 border-amber-300/50">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-xl bg-gradient-to-r from-amber-400 to-rose-400 text-white shadow-md">
                        <FaMapMarkerAlt className="w-5 h-5" aria-hidden="true" />
                      </div>
                      <div>
                        {/* 🔍 SEO: H2 for each city group */}
                        <h2
                          id={`city-heading-${group.citySlug}`}
                          className="text-xl md:text-2xl font-bold text-gray-900"
                        >
                          Projects in {group.cityName}
                        </h2>
                        <p className="text-sm text-gray-500">
                          {group.count}{' '}
                          {group.count === 1 ? 'project' : 'projects'} available
                        </p>
                      </div>
                    </div>
                    <span className="px-3 py-1 bg-amber-400/30 backdrop-blur-md border border-amber-300/50 text-amber-900 text-sm font-semibold rounded-full shadow-sm">
                      {group.count} {group.count === 1 ? 'Listing' : 'Listings'}
                    </span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                    {group.projects.map((project) => (
                      <ProjectCard
                        key={project.id}
                        project={project}
                        onSelect={handleProjectSelect}
                      />
                    ))}
                  </div>

                  {groupIndex < projectsByCity.length - 1 && (
                    <div className="mt-12 h-px bg-gradient-to-r from-transparent via-gray-300/50 to-transparent" />
                  )}
                </section>
              ))}
            </div>
          ) : (
            <div className={`text-center py-16 ${glassSurface}`}>
              <svg
                className="mx-auto h-12 w-12 text-gray-400 mb-4"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              <p className="text-gray-700 text-lg font-medium">No projects found</p>
              <p className="text-gray-500 text-sm mt-1">
                {hasActiveFilters
                  ? 'Try adjusting your search or filter criteria.'
                  : selectedCompany
                  ? 'No projects found for this company.'
                  : 'No projects available at the moment.'}
              </p>
              {hasActiveFilters && (
                <button
                  onClick={handleClearFilters}
                  className="mt-4 px-5 py-2 bg-amber-400/70 backdrop-blur-md border border-amber-300/60 hover:bg-amber-500/80 text-white font-medium rounded-lg shadow-md transition-colors"
                >
                  Clear all filters
                </button>
              )}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default ProjectsPage;